import { useState, useCallback } from 'react';
import { useToast } from '@/hooks/use-toast';
import { useAnalytics } from '@/contexts/AnalyticsContext';
import { enhancedRiskAssessmentService } from '@/services/enhancedRiskAssessmentService';
import { 
  generateAmortizationChartData, 
  generateCostScenariosChartData, 
  generateEquityGrowthChartData,
  type MortgageInputs 
} from '@/utils/chartDataGenerators';

export type ReportFormat = 'txt' | 'csv' | 'json' | 'html' | 'xlsx';

interface ReportData {
  formData: any;
  results: any;
  analytics?: any;
  supremeIntelligence?: any;
  eligibilityResults?: any;
  comparisonResults?: any;
  userInfo?: {
    name?: string;
    email?: string;
  };
  completionStatus?: any;
}

export const useMultiFormatReportGeneration = () => {
  const { toast } = useToast();
  const { trackReportGeneration } = useAnalytics();
  const [isGenerating, setIsGenerating] = useState(false);

  const formatCurrency = (value: number) => 
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);

  const formatPercent = (value: number) => 
    new Intl.NumberFormat('en-US', { style: 'percent', minimumFractionDigits: 2 }).format(value / 100);

  const generateTextReport = useCallback((data: ReportData): string => {
    const { formData, results, analytics, supremeIntelligence, eligibilityResults, comparisonResults, userInfo } = data;
    
    let report = `EQUIFORGE™ PROFESSIONAL REAL ESTATE REPORT\n`;
    report += `${'='.repeat(60)}\n`;
    report += `Advanced Home Buying Intelligence for Real Estate Professionals\n`;
    report += `${'='.repeat(60)}\n\n`;
    
    // User Information
    const getUserDisplayName = () => {
      if (userInfo?.name) return userInfo.name;
      if (formData.personalDetails?.firstName && formData.personalDetails?.lastName) {
        return `${formData.personalDetails.firstName} ${formData.personalDetails.lastName}`;
      }
      if (formData.personalDetails?.firstName) return formData.personalDetails.firstName;
      return 'EquiForge User';
    };
    
    report += `Prepared for: ${getUserDisplayName()}\n`;
    if (userInfo?.email) {
      report += `Email: ${userInfo.email}\n`;
    }
    report += `Generated: ${new Date().toLocaleString()}\n`;
    report += `Report ID: RPT-${Date.now()}\n\n`;

    // EXECUTIVE SUMMARY
    report += `EXECUTIVE SUMMARY\n`;
    report += `${'='.repeat(20)}\n`;
    if (results?.keyMetrics) {
      report += `• Recommended Loan Program: ${formData.propertyLoan?.loanType?.toUpperCase() || 'To Be Determined'}\n`;
      report += `• Maximum Purchase Price: ${formatCurrency(results.maxPurchasePrice || 0)}\n`;
      report += `• Monthly Payment Range: ${formatCurrency(results.keyMetrics.monthlyPayment || 0)}\n`;
      report += `• Required Down Payment: ${formatCurrency(results.keyMetrics.cashToClose || 0)}\n`;
      report += `• Approval Probability: ${analytics?.riskAssessment?.riskLevel === 'Low' ? 'High (85-95%)' : analytics?.riskAssessment?.riskLevel === 'Medium' ? 'Moderate (70-84%)' : 'Requires Review (50-69%)'}\n`;
    }
    report += `\n`;

    // CLIENT PROFILE
    report += `CLIENT PROFILE\n`;
    report += `${'='.repeat(15)}\n`;
    report += `Client Name: ${getUserDisplayName()}\n`;
    if (userInfo?.email) report += `Contact: ${userInfo.email}\n`;
    report += `Annual Income: ${formatCurrency(formData.primaryIncome?.annualIncome || 0)}\n`;
    report += `Credit Score: ${formData.personalDetails?.creditScore || 'Not provided'}\n`;
    report += `Property Type: ${formData.personalDetails?.propertyType || 'Not specified'}\n`;
    report += `Location: ${formData.personalDetails?.state || 'Not specified'}\n`;
    report += `First-Time Buyer: ${formData.personalDetails?.isFirstTimeBuyer ? 'Yes' : 'No'}\n`;
    // Check veteran status from multiple sources
    const isVeteran = formData.vaEligibility?.veteranStatus || 
                     (formData.personalDetails as any)?.veteranStatus === 'veteran' ||
                     (formData.personalDetails as any)?.veteranStatus === 'activeService' ||
                     formData.propertyLoan?.loanType === 'va';
    report += `Veteran Status: ${isVeteran ? 'Yes' : 'No'}\n\n`;

    // FINANCIAL PRE-QUALIFICATION
    report += `FINANCIAL PRE-QUALIFICATION ANALYSIS\n`;
    report += `${'='.repeat(40)}\n`;
    
    // Income Analysis
    report += `INCOME VERIFICATION:\n`;
    report += `${'-'.repeat(20)}\n`;
    report += `Primary Income Type: ${formData.primaryIncome?.incomeType === 'salary' ? 'W-2 Salary' : 'Hourly/Contract'}\n`;
    report += `Annual Income: ${formatCurrency(formData.primaryIncome?.annualIncome || 0)}\n`;
    if (formData.primaryIncome?.additionalIncome) {
      report += `Additional Income: ${formatCurrency(formData.primaryIncome.additionalIncome)}\n`;
    }
    if (formData.coBorrowerIncome?.enabled) {
      report += `Co-borrower Income: ${formatCurrency(formData.coBorrowerIncome.annualIncome || 0)}\n`;
    }
    report += `Monthly Income: ${formatCurrency((formData.primaryIncome?.annualIncome || 0) / 12)}\n`;
    report += `Income Stability: ${formData.primaryIncome?.incomeType === 'salary' ? 'Stable (W-2)' : 'Variable (Requires 2yr history)'}\n\n`;

    // Credit Analysis
    const creditScore = formData.personalDetails?.creditScore || 650;
    report += `CREDIT PROFILE:\n`;
    report += `${'-'.repeat(15)}\n`;
    report += `Current Score: ${creditScore}\n`;
    report += `Score Range: ${creditScore >= 740 ? 'Excellent (740+)' : creditScore >= 670 ? 'Good (670-739)' : creditScore >= 580 ? 'Fair (580-669)' : 'Poor (below 580)'}\n`;
    if (creditScore < 740) {
      report += `Improvement Potential: ${740 - creditScore} points\n`;
      report += `Recommended Actions:\n`;
      if (creditScore < 620) {
        report += `  • Pay down credit card balances immediately\n`;
        report += `  • Dispute any credit report errors\n`;
        report += `  • Timeline: 3-6 months for significant improvement\n`;
      } else if (creditScore < 680) {
        report += `  • Make all payments on time\n`;
        report += `  • Avoid new credit inquiries\n`;
        report += `  • Timeline: 30-90 days for modest improvement\n`;
      } else {
        report += `  • Keep credit utilization below 10%\n`;
        report += `  • Maintain older credit accounts\n`;
      }
    }
    report += `\n`;

    // Down Payment Analysis
    report += `DOWN PAYMENT & RESERVES:\n`;
    report += `${'-'.repeat(25)}\n`;
    const downPaymentAmount = formData.propertyLoan?.downPaymentAmount || 0;
    const downPaymentPercent = formData.propertyLoan?.downPaymentPercentage || 0;
    report += `Available Down Payment: ${formatCurrency(downPaymentAmount)}\n`;
    report += `Down Payment Percentage: ${downPaymentPercent}%\n`;
    if (downPaymentPercent >= 20) {
      report += `PMI Status: Not required (20%+ down)\n`;
    } else {
      report += `PMI Status: Required (less than 20% down)\n`;
    }
    
    const loanType = formData.propertyLoan?.loanType || 'conventional';
    const requiredReserves = loanType === 'conventional' ? 2 : loanType === 'fha' ? 1 : loanType === 'va' ? 0 : 1;
    report += `Required Reserves: ${requiredReserves} months PITI\n`;
    report += `Recommended Reserves: 6 months PITI\n\n`;

    // LOAN PROGRAM OPTIMIZATION
    report += `LOAN PROGRAM OPTIMIZATION MATRIX\n`;
    report += `${'='.repeat(35)}\n`;
    
    // Program Recommendations
    let primaryProgram = 'Conventional 30-Year Fixed';
    if (formData.vaEligibility?.veteranStatus) primaryProgram = 'VA 30-Year Fixed';
    else if (formData.personalDetails?.isRuralArea) primaryProgram = 'USDA Rural Development';
    else if (creditScore < 650) primaryProgram = 'FHA 30-Year Fixed';
    
    report += `PRIMARY RECOMMENDATION: ${primaryProgram}\n`;
    report += `${'-'.repeat(30)}\n`;
    
    // Program-specific details
    if (primaryProgram.includes('VA')) {
      report += `Advantages:\n`;
      report += `  • No down payment required\n`;
      report += `  • No PMI required\n`;
      report += `  • Competitive interest rates\n`;
      report += `  • No prepayment penalties\n`;
      report += `Requirements:\n`;
      report += `  • Certificate of Eligibility (COE)\n`;
      report += `  • Primary residence only\n`;
      report += `  • VA property condition requirements\n`;
      report += `Processing Time: 30-45 days\n\n`;
    } else if (primaryProgram.includes('FHA')) {
      report += `Advantages:\n`;
      report += `  • Low down payment (3.5%)\n`;
      report += `  • Flexible credit requirements\n`;
      report += `  • Assumable loans\n`;
      report += `Requirements:\n`;
      report += `  • Minimum credit score 580\n`;
      report += `  • MIP (mortgage insurance) required\n`;
      report += `  • FHA property condition standards\n`;
      report += `Processing Time: 30-45 days\n\n`;
    } else if (primaryProgram.includes('USDA')) {
      report += `Advantages:\n`;
      report += `  • No down payment required\n`;
      report += `  • Below-market interest rates\n`;
      report += `  • Flexible credit requirements\n`;
      report += `Requirements:\n`;
      report += `  • Property in USDA-eligible rural area\n`;
      report += `  • Income limits apply\n`;
      report += `  • Primary residence only\n`;
      report += `Processing Time: 45-60 days\n\n`;
    } else {
      report += `Advantages:\n`;
      report += `  • No mortgage insurance with 20% down\n`;
      report += `  • Competitive rates\n`;
      report += `  • No property restrictions\n`;
      report += `Requirements:\n`;
      report += `  • Minimum credit score 620\n`;
      report += `  • DTI ≤ 45% typically\n`;
      report += `  • 5-20% down payment\n`;
      report += `Processing Time: 30-45 days\n\n`;
    }

    // PROPERTY SEARCH PARAMETERS
    report += `PROPERTY SEARCH PARAMETERS\n`;
    report += `${'='.repeat(30)}\n`;
    const maxPrice = results?.maxPurchasePrice || formData.propertyLoan?.homePrice || 400000;
    const comfortableMax = maxPrice * 0.85;
    
    report += `PRICE RANGES:\n`;
    report += `${'-'.repeat(15)}\n`;
    report += `Comfortable Range: ${formatCurrency(comfortableMax * 0.7)} - ${formatCurrency(comfortableMax)}\n`;
    report += `Maximum Range: ${formatCurrency(comfortableMax)} - ${formatCurrency(maxPrice)}\n`;
    report += `Buffer for Bidding: ${formatCurrency(maxPrice * 0.05)}\n\n`;
    
    report += `ELIGIBLE PROPERTY TYPES:\n`;
    report += `${'-'.repeat(25)}\n`;
    const eligibleTypes = ['Single-family homes', 'Townhomes', 'Condominiums'];
    if (loanType === 'conventional') eligibleTypes.push('Investment properties', 'Luxury homes');
    if (loanType === 'fha') eligibleTypes.push('Manufactured homes (if FHA-approved)');
    eligibleTypes.forEach(type => report += `  • ${type}\n`);
    
    if (loanType === 'va' || loanType === 'fha') {
      report += `\nProperty Condition Requirements:\n`;
      report += `  • Move-in ready condition preferred\n`;
      report += `  • Maximum repairs: $5,000\n`;
      report += `  • Must meet program safety/habitability standards\n`;
    }
    report += `\n`;

    // MARKET INTELLIGENCE
    report += `MARKET INTELLIGENCE & STRATEGY\n`;
    report += `${'='.repeat(30)}\n`;
    report += `Current Market Conditions:\n`;
    report += `  • Average Days on Market: 30 days\n`;
    report += `  • Inventory Level: Moderate\n`;
    report += `  • Price Direction: Stable\n`;
    report += `  • Expected Competition: 2-3 offers per property\n\n`;
    
    report += `Competitive Strategy:\n`;
    report += `  • Pre-approval strength: Major advantage\n`;
    report += `  • Recommended offer approach: Market value with standard contingencies\n`;
    report += `  • Contingency recommendations: Inspection (10 days), Appraisal, Financing (30 days)\n`;
    report += `  • Escalation strategy: Competitive but measured\n\n`;

    // RISK ASSESSMENT
    report += `RISK ASSESSMENT & MITIGATION\n`;
    report += `${'='.repeat(30)}\n`;
    
    let approvalProbability = 85; // Base
    if (creditScore < 620) approvalProbability -= 20;
    else if (creditScore < 680) approvalProbability -= 10;
    if (downPaymentPercent < 10) approvalProbability -= 10;
    if (formData.primaryIncome?.incomeType !== 'salary') approvalProbability -= 5;
    
    report += `Approval Probability: ${Math.max(50, approvalProbability)}%\n`;
    report += `Risk Level: ${approvalProbability >= 85 ? 'Low' : approvalProbability >= 70 ? 'Medium' : 'High'}\n\n`;
    
    report += `Success Factors:\n`;
    if (creditScore >= 740) report += `  ✓ Excellent credit score\n`;
    if (formData.primaryIncome?.incomeType === 'salary') report += `  ✓ Stable salary income\n`;
    if (downPaymentPercent >= 20) report += `  ✓ Substantial down payment\n`;
    if (formData.vaEligibility?.veteranStatus) report += `  ✓ VA loan eligibility\n`;
    
    if (approvalProbability < 85) {
      report += `\nAreas for Improvement:\n`;
      if (creditScore < 680) report += `  • Improve credit score\n`;
      if (downPaymentPercent < 10) report += `  • Increase down payment if possible\n`;
      if (formData.primaryIncome?.incomeType !== 'salary') report += `  • Ensure stable income documentation\n`;
    }
    report += `\n`;

    // ACTION PLAN
    report += `IMMEDIATE ACTION PLAN\n`;
    report += `${'='.repeat(20)}\n`;
    const today = new Date();
    const nextWeek = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000);
    const twoWeeks = new Date(today.getTime() + 14 * 24 * 60 * 60 * 1000);
    
    report += `WEEK 1 (by ${nextWeek.toLocaleDateString()}):\n`;
    report += `  □ Complete formal loan application\n`;
    report += `  □ Gather pay stubs (2 most recent)\n`;
    report += `  □ Collect bank statements (2 months)\n`;
    report += `  □ Schedule lender meeting\n\n`;
    
    report += `WEEK 2 (by ${twoWeeks.toLocaleDateString()}):\n`;
    report += `  □ Submit tax returns (2 years)\n`;
    report += `  □ Provide W-2 forms (2 years)\n`;
    report += `  □ Complete credit report review\n`;
    report += `  □ Begin property search setup\n\n`;
    
    report += `WEEKS 3-4:\n`;
    report += `  □ Receive pre-approval letter\n`;
    report += `  □ Activate property searches\n`;
    report += `  □ Schedule property viewings\n`;
    report += `  □ Prepare offer strategy\n\n`;

    // PROFESSIONAL NETWORK
    report += `PROFESSIONAL NETWORK & REFERRALS\n`;
    report += `${'='.repeat(35)}\n`;
    report += `Recommended Service Providers:\n`;
    report += `\n`;
    report += `LENDERS:\n`;
    report += `  • Primary Lender: [Agent to provide specific contact]\n`;
    report += `  • Specialty: ${loanType.toUpperCase()} loans\n`;
    report += `  • Processing Time: 30-45 days\n`;
    report += `  • Program Expertise: ${loanType}, First-time buyer programs\n\n`;
    
    report += `INSPECTION & APPRAISAL:\n`;
    report += `  • Home Inspector: [Agent to coordinate]\n`;
    report += `  • Estimated Cost: $400-600\n`;
    report += `  • Appraiser: [Lender coordinated]\n`;
    report += `  • Estimated Cost: $500-700\n\n`;
    
    report += `TITLE & CLOSING:\n`;
    report += `  • Title Company: [Agent to recommend]\n`;
    report += `  • Estimated Fees: $1,200-2,500\n`;
    report += `  • Closing Coordination: Full service\n\n`;

    // Key Metrics (matching Enhanced Results Summary)
    if (results?.keyMetrics) {
      report += `KEY METRICS\n`;
      report += `${'-'.repeat(15)}\n`;
      report += `Monthly Payment: ${formatCurrency(results.keyMetrics.monthlyPayment)}\n`;
      report += `Total Interest: ${formatCurrency(results.keyMetrics.totalInterest)}\n`;
      report += `Net Closing Costs: ${formatCurrency(results.keyMetrics.netClosingCosts)}\n`;
      report += `Cash to Close: ${formatCurrency(results.keyMetrics.cashToClose)}\n\n`;
    }

    // Monthly Payment Breakdown
    if (results?.monthlyBreakdown) {
      report += `MONTHLY PAYMENT BREAKDOWN\n`;
      report += `${'-'.repeat(30)}\n`;
      report += `Principal & Interest: ${formatCurrency(results.monthlyBreakdown.principalAndInterest)}\n`;
      report += `Property Taxes: ${formatCurrency(results.monthlyBreakdown.propertyTaxes)}\n`;
      report += `Home Insurance: ${formatCurrency(results.monthlyBreakdown.homeInsurance)}\n`;
      if (results.monthlyBreakdown.pmi > 0) {
        report += `PMI: ${formatCurrency(results.monthlyBreakdown.pmi)}\n`;
      }
      if (results.monthlyBreakdown.hoaFees > 0) {
        report += `HOA Fees: ${formatCurrency(results.monthlyBreakdown.hoaFees)}\n`;
      }
      report += `\n`;
    }

    // Loan Analysis
    if (results?.loanAnalysis) {
      report += `LOAN ANALYSIS\n`;
      report += `${'-'.repeat(15)}\n`;
      report += `Home Price: ${formatCurrency(formData.propertyDetails?.homePrice || 0)}\n`;
      report += `Loan Amount: ${formatCurrency(results.loanAnalysis.loanAmount)}\n`;
      report += `Loan Type: ${results.loanAnalysis.loanType?.toUpperCase() || 'Not specified'}\n`;
      report += `Interest Rate: ${formatPercent(results.loanAnalysis.interestRate || 0)}\n`;
      report += `Loan Term: ${results.loanAnalysis.loanTerm || 30} years\n`;
      report += `Loan-to-Value: ${formatPercent(results.loanAnalysis.loanToValue || 0)}\n`;
      report += `Total Amount Paid: ${formatCurrency(results.loanAnalysis.totalAmountPaid || 0)}\n\n`;
    }

    // Property Details
    if (formData.propertyDetails) {
      report += `PROPERTY DETAILS\n`;
      report += `${'-'.repeat(20)}\n`;
      report += `Property Type: ${formData.propertyDetails.propertyType || 'Not specified'}\n`;
      report += `Location: ${formData.propertyDetails.city || ''}, ${formData.propertyDetails.state || ''}\n\n`;
    }

    // Loan Program Eligibility
    if (eligibilityResults) {
      report += `LOAN PROGRAM ELIGIBILITY\n`;
      report += `${'-'.repeat(30)}\n`;
      Object.entries(eligibilityResults).forEach(([program, eligibility]) => {
        const eligible = (eligibility as any)?.eligible ? 'YES' : 'NO';
        const reason = (eligibility as any)?.reason || '';
        report += `${program.toUpperCase()}: ${eligible}${reason ? ` - ${reason}` : ''}\n`;
      });
      report += `\n`;
    }

    // Analytics Dashboard
    if (analytics) {
      report += `ANALYTICS DASHBOARD\n`;
      report += `${'='.repeat(20)}\n\n`;
      
      if (analytics.riskAssessment) {
        report += `RISK ASSESSMENT\n`;
        report += `${'-'.repeat(15)}\n`;
        report += `Risk Level: ${analytics.riskAssessment.riskLevel}\n`;
        report += `Loan-to-Value: ${formatPercent(analytics.riskAssessment.loanToValue)}\n`;
        report += `Down Payment Ratio: ${formatPercent(analytics.riskAssessment.downPaymentRatio)}\n`;
        report += `Closing Cost Ratio: ${formatPercent(analytics.riskAssessment.closingCostRatio)}\n\n`;
      }
      
      if (analytics.costAnalysis) {
        report += `COST ANALYSIS\n`;
        report += `${'-'.repeat(15)}\n`;
        report += `Total Cost of Ownership: ${formatCurrency(analytics.costAnalysis.totalCostOfOwnership)}\n`;
        report += `Interest to Loan Ratio: ${formatPercent(analytics.costAnalysis.interestToLoanRatio)}\n\n`;
      }
      
      if (analytics.incomeRequirements) {
        report += `INCOME REQUIREMENTS\n`;
        report += `${'-'.repeat(20)}\n`;
        report += `Recommended Annual Income: ${formatCurrency(analytics.incomeRequirements.recommendedAnnualIncome)}\n`;
        report += `Recommended Monthly Income: ${formatCurrency(analytics.incomeRequirements.recommendedMonthlyIncome)}\n`;
        report += `Target DTI Ratio: ${formatPercent(analytics.incomeRequirements.debtToIncomeRatio)}\n\n`;
      }
      
      if (analytics.keyInsights?.length > 0) {
        report += `KEY INSIGHTS\n`;
        report += `${'-'.repeat(15)}\n`;
        analytics.keyInsights.forEach((insight: any) => {
          const statusIcon = insight.status === 'good' ? '✓' : insight.status === 'warning' ? '⚠' : '⚠';
          report += `${statusIcon} ${insight.title}: ${insight.message}\n`;
        });
        report += `\n`;
      }
    }

    // Supreme Intelligence Analysis
    if (supremeIntelligence) {
      report += `SUPREME INTELLIGENCE ANALYSIS\n`;
      report += `${'='.repeat(30)}\n\n`;
      
      if (supremeIntelligence.availablePrograms?.length > 0) {
        report += `AVAILABLE LOAN PROGRAMS\n`;
        report += `${'-'.repeat(25)}\n`;
        supremeIntelligence.availablePrograms.forEach((program: any) => {
          report += `${program.name}${program.recommended ? ' (RECOMMENDED)' : ''}\n`;
          report += `  Monthly Payment: ${formatCurrency(program.monthlyPayment)}\n`;
          report += `  Down Payment: ${formatCurrency(program.downPayment)} (${formatPercent(program.downPercent)})\n`;
          report += `  APR: ${formatPercent(program.apr)}\n`;
          report += `  Total Cost: ${formatCurrency(program.totalCost)}\n`;
          if (program.savings > 0) {
            report += `  Potential Savings: ${formatCurrency(program.savings)}\n`;
          }
          report += `  Eligible: ${program.eligible ? 'YES' : 'NO'}\n\n`;
        });
      }
      
      if (supremeIntelligence.optimization) {
        report += `OPTIMIZATION OPPORTUNITIES\n`;
        report += `${'-'.repeat(25)}\n`;
        if (supremeIntelligence.optimization.extraPrincipalSavings > 0) {
          report += `Extra Principal Savings: ${formatCurrency(supremeIntelligence.optimization.extraPrincipalSavings)}\n`;
        }
        if (supremeIntelligence.optimization.refinancingOpportunity > 0) {
          report += `Refinancing Opportunity: ${formatCurrency(supremeIntelligence.optimization.refinancingOpportunity)}/month\n`;
        }
        
        if (supremeIntelligence.optimization.shortTermStrategies?.length > 0) {
          report += `\nShort-term Strategies:\n`;
          supremeIntelligence.optimization.shortTermStrategies.forEach((strategy: string) => {
            report += `• ${strategy}\n`;
          });
        }
        
        if (supremeIntelligence.optimization.longTermStrategies?.length > 0) {
          report += `\nLong-term Strategies:\n`;
          supremeIntelligence.optimization.longTermStrategies.forEach((strategy: string) => {
            report += `• ${strategy}\n`;
          });
        }
        report += `\n`;
      }
    }

    // Comparison Results
    if (comparisonResults && comparisonResults.length > 0) {
      report += `LOAN COMPARISON\n`;
      report += `${'-'.repeat(20)}\n`;
      comparisonResults.forEach((loan: any, index: number) => {
        report += `Option ${index + 1} - ${loan.loanType?.toUpperCase()}\n`;
        report += `  Max Purchase Price: ${formatCurrency(loan.maxPurchasePrice || 0)}\n`;
        report += `  Monthly Payment: ${formatCurrency(loan.monthlyPayment || 0)}\n`;
        report += `  Total Cost (30yr): ${formatCurrency(loan.totalCost || 0)}\n`;
        if (loan.recommended) report += `  *** RECOMMENDED ***\n`;
        report += `\n`;
      });
    }

    report += `\nThis report was generated by EquiForge™ - Advanced Mortgage Intelligence Platform\n`;
    
    return report;
  }, [formatCurrency, formatPercent]);

  const generateCSVReport = useCallback((data: ReportData): string => {
    const { formData, results, analytics, supremeIntelligence, eligibilityResults, comparisonResults } = data;
    
    let csv = 'Category,Field,Value\n';
    
    // Personal Information
    csv += `Personal,Annual Income,"${formatCurrency(formData.primaryIncome?.annualIncome || 0)}"\n`;
    csv += `Personal,Credit Score,${formData.personalDetails?.creditScore || ''}\n`;
    csv += `Personal,State,${formData.personalDetails?.state || ''}\n`;
    csv += `Personal,Veteran Status,${formData.personalDetails?.veteranStatus || ''}\n`;

    // Loan Preferences
    csv += `Loan,Loan Type,${formData.propertyLoan?.loanType || ''}\n`;
    csv += `Loan,Down Payment,"${formatPercent(formData.propertyLoan?.downPaymentPercentage || 0)}"\n`;
    csv += `Loan,Interest Rate,"${formatPercent(formData.propertyLoan?.interestRate || 0)}"\n`;
    csv += `Loan,Loan Term,${formData.propertyLoan?.loanTerm || 30} years\n`;

    // Results
    if (results) {
      csv += `Results,Maximum Purchase Price,"${formatCurrency(results.maxPurchasePrice || 0)}"\n`;
      csv += `Results,Maximum Monthly Payment,"${formatCurrency(results.maxMonthlyPayment || 0)}"\n`;
      csv += `Results,Debt-to-Income Ratio,"${formatPercent(results.debtToIncomeRatio || 0)}"\n`;
    }

    // Analytics
    if (analytics?.riskAssessment) {
      csv += `Analytics,Risk Level,${analytics.riskAssessment.riskLevel}\n`;
      csv += `Analytics,Loan-to-Value,"${formatPercent(analytics.riskAssessment.loanToValue)}"\n`;
      csv += `Analytics,Down Payment Ratio,"${formatPercent(analytics.riskAssessment.downPaymentRatio)}"\n`;
      csv += `Analytics,Closing Cost Ratio,"${formatPercent(analytics.riskAssessment.closingCostRatio)}"\n`;
    }

    if (analytics?.costAnalysis) {
      csv += `Cost Analysis,Total Cost of Ownership,"${formatCurrency(analytics.costAnalysis.totalCostOfOwnership)}"\n`;
      csv += `Cost Analysis,Interest to Loan Ratio,"${formatPercent(analytics.costAnalysis.interestToLoanRatio)}"\n`;
    }

    // Supreme Intelligence Programs
    if (supremeIntelligence?.availablePrograms?.length > 0) {
      supremeIntelligence.availablePrograms.forEach((program: any, index: number) => {
        csv += `Program ${index + 1},Name,${program.name}\n`;
        csv += `Program ${index + 1},Monthly Payment,"${formatCurrency(program.monthlyPayment)}"\n`;
        csv += `Program ${index + 1},Down Payment,"${formatCurrency(program.downPayment)}"\n`;
        csv += `Program ${index + 1},APR,"${formatPercent(program.apr)}"\n`;
        csv += `Program ${index + 1},Eligible,${program.eligible ? 'YES' : 'NO'}\n`;
        csv += `Program ${index + 1},Recommended,${program.recommended ? 'YES' : 'NO'}\n`;
      });
    }

    // Eligibility
    if (eligibilityResults) {
      Object.entries(eligibilityResults).forEach(([program, eligibility]) => {
        const eligible = (eligibility as any)?.eligible ? 'YES' : 'NO';
        csv += `Eligibility,${program.toUpperCase()},${eligible}\n`;
      });
    }

    return csv;
  }, [formatCurrency, formatPercent]);

  const generateJSONReport = useCallback((data: ReportData): string => {
    const reportData = {
      metadata: {
        title: 'EquiForge™ Unified Buying Power Report',
        generatedAt: new Date().toISOString(),
        userInfo: data.userInfo,
        version: '1.0'
      },
      personalInformation: {
        annualIncome: data.formData.primaryIncome?.annualIncome || 0,
        creditScore: data.formData.personalDetails?.creditScore || null,
        state: data.formData.personalDetails?.state || null,
        veteranStatus: data.formData.personalDetails?.veteranStatus || null
      },
      loanPreferences: {
        loanType: data.formData.propertyLoan?.loanType || null,
        downPaymentPercentage: data.formData.propertyLoan?.downPaymentPercentage || 0,
        interestRate: data.formData.propertyLoan?.interestRate || 0,
        loanTerm: data.formData.propertyLoan?.loanTerm || 30
      },
      calculationResults: data.results || {},
      analytics: data.analytics || {},
      supremeIntelligence: data.supremeIntelligence || {},
      eligibilityResults: data.eligibilityResults || {},
      comparisonResults: data.comparisonResults || [],
      completionStatus: data.completionStatus || {}
    };

    return JSON.stringify(reportData, null, 2);
  }, []);

  const generateHTMLReport = useCallback((data: ReportData): string => {
    const { formData, results, analytics, supremeIntelligence, eligibilityResults, comparisonResults, userInfo } = data;
    
    // Check veteran status from multiple sources for accuracy
    const isVeteran = formData.vaEligibility?.veteranStatus || 
                     (formData.personalDetails as any)?.veteranStatus === 'veteran' ||
                     (formData.personalDetails as any)?.veteranStatus === 'activeService' ||
                     formData.propertyLoan?.loanType === 'va';
    
    let html = `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <link rel="preconnect" href="https://fonts.googleapis.com">
    <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
    <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Space+Mono:wght@400;700&display=swap" rel="stylesheet">
    <title>EquiForge™ Professional Real Estate Report</title>
    <style>
        /* Color System - High Contrast for All Contexts */
        :root {
            --bg-primary: hsl(0 0% 100%);
            --bg-secondary: hsl(210 40% 98%);
            --bg-accent: hsl(210 40% 96%);
            --text-primary: hsl(222.2 84% 4.9%);
            --text-secondary: hsl(215.4 16.3% 46.9%);
            --text-muted: hsl(215.4 16.3% 56.9%);
            --border-primary: hsl(214.3 31.8% 91.4%);
            --border-secondary: hsl(215 13.8% 84.1%);
            --accent-blue: hsl(221.2 83.2% 53.3%);
            --accent-green: hsl(142.1 76.2% 36.3%);
            --accent-red: hsl(0 84.2% 60.2%);
            --accent-purple: hsl(262.1 83.3% 57.8%);
            --accent-yellow: hsl(47.9 95.8% 53.1%);
        }

        body { 
            font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; 
            line-height: 1.6; 
            margin: 0; 
            padding: 20px;
            background: var(--bg-primary); 
            color: var(--text-primary);
            font-size: 16px;
            font-weight: 400;
        }
        .container { max-width: 1200px; margin: 0 auto; }
        .header { 
            text-align: center; 
            margin-bottom: 40px; 
            padding: 30px; 
            background: var(--bg-secondary);
            border: 1px solid var(--border-primary);
            border-radius: 12px;
        }
        .header h1 { 
            color: var(--text-primary); 
            margin: 0; 
            font-size: 2.5em; 
            font-weight: 700;
        }
        .header p { color: var(--text-secondary); margin: 10px 0; }
        
        .executive-summary {
            background: linear-gradient(135deg, var(--accent-blue), var(--accent-purple));
            color: white;
            padding: 30px;
            border-radius: 12px;
            margin: 30px 0;
        }
        
        .key-metrics {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
            gap: 20px;
            margin: 30px 0;
        }
        
        .metric-card {
            background: var(--bg-secondary);
            border: 1px solid var(--border-primary);
            border-radius: 12px;
            padding: 24px;
            transition: all 0.3s ease;
        }
        .metric-card:hover {
            border-color: var(--border-secondary);
            transform: translateY(-2px);
        }
        
        .metric-card.monthly-payment { border-left: 4px solid var(--accent-blue); }
        .metric-card.total-interest { border-left: 4px solid var(--accent-red); }
        .metric-card.closing-costs { border-left: 4px solid var(--accent-purple); }
        .metric-card.cash-close { border-left: 4px solid var(--accent-green); }
        .metric-card.approval { border-left: 4px solid var(--accent-yellow); }
        
        .metric-label {
            font-size: 0.875rem;
            color: var(--text-muted);
            margin-bottom: 8px;
            text-transform: uppercase;
            letter-spacing: 0.05em;
        }
        
        .metric-value {
            font-size: 2rem;
            font-weight: 700;
            color: var(--text-primary);
        }
        
        .metric-value.small {
            font-size: 1.5rem;
        }
        
        .section {
            margin: 30px 0;
            background: var(--bg-secondary);
            border: 1px solid var(--border-primary);
            border-radius: 12px;
            padding: 24px;
        }
        
        .section h2 {
            color: var(--text-primary);
            margin-top: 0;
            margin-bottom: 20px;
            font-size: 1.5em;
            font-weight: 600;
            border-bottom: 1px solid var(--border-primary);
            padding-bottom: 12px;
        }
        
        .section h3 {
            color: var(--accent-blue);
            margin-top: 25px;
            margin-bottom: 15px;
            font-size: 1.2em;
            font-weight: 600;
        }
        
        .loan-eligibility {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
            gap: 15px;
            margin: 20px 0;
        }
        
        .eligibility-card {
            padding: 16px;
            border-radius: 8px;
            border: 1px solid var(--border-primary);
            text-align: center;
            background: var(--bg-accent);
        }
        
        .eligibility-card.eligible {
            background: hsl(142.1 76.2% 36.3% / 0.1);
            border-color: var(--accent-green);
        }
        
        .eligibility-card.not-eligible {
            background: hsl(0 84.2% 60.2% / 0.1);
            border-color: var(--accent-red);
        }
        
        .eligibility-status {
            font-weight: 700;
            font-size: 1.1rem;
            margin-bottom: 8px;
        }
        
        .eligibility-status.eligible {
            color: var(--accent-green);
        }
        
        .eligibility-status.not-eligible {
            color: var(--accent-red);
        }
        
        .loan-program {
            font-size: 0.9rem;
            color: var(--text-secondary);
        }
        }
        
        .breakdown-item {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 12px 0;
            border-bottom: 1px solid var(--border-primary);
        }
        .breakdown-item:last-child { border-bottom: none; }
        
        .breakdown-label {
            color: var(--text-secondary);
            font-size: 0.95rem;
        }
        
        .breakdown-value {
            color: var(--text-primary);
            font-weight: 600;
        }
        
        .progress-bar {
            width: 100%;
            height: 8px;
            background: var(--border-secondary);
            border-radius: 4px;
            margin: 8px 0;
            overflow: hidden;
        }
        
        .progress-fill {
            height: 100%;
            background: var(--accent-blue);
            border-radius: 4px;
            transition: width 0.3s ease;
        }
        
        .loan-details {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
            gap: 20px;
        }
        
        .detail-item {
            padding: 16px;
            background: var(--bg-accent);
            border: 1px solid var(--border-primary);
            border-radius: 8px;
        }
        
        .detail-label {
            font-size: 0.875rem;
            color: var(--text-secondary);
            margin-bottom: 4px;
        }
        
        .detail-value {
            font-size: 1.1rem;
            font-weight: 600;
            color: var(--text-primary);
        }
        
        .footer {
            text-align: center;
            margin-top: 50px;
            padding: 30px;
            background: var(--bg-secondary);
            border: 1px solid var(--border-primary);
            border-radius: 12px;
            color: var(--text-secondary);
        }
        
        /* Print and Export Media Queries - High Contrast */
        @media print, screen and (forced-colors: active) { 
            :root {
                --bg-primary: hsl(0 0% 100%);
                --bg-secondary: hsl(0 0% 98%);
                --bg-accent: hsl(0 0% 96%);
                --text-primary: hsl(0 0% 0%);
                --text-secondary: hsl(0 0% 25%);
                --text-muted: hsl(0 0% 40%);
                --border-primary: hsl(0 0% 85%);
                --border-secondary: hsl(0 0% 70%);
                --accent-blue: hsl(221.2 83.2% 40%);
                --accent-green: hsl(142.1 76.2% 30%);
                --accent-red: hsl(0 84.2% 50%);
                --accent-purple: hsl(262.1 83.3% 45%);
                --accent-yellow: hsl(47.9 95.8% 40%);
            }
            
            body { 
                margin: 20px; 
                background: var(--bg-primary) !important; 
                color: var(--text-primary) !important; 
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
            }
            
            .section { 
                break-inside: avoid; 
                background: var(--bg-primary) !important;
                color: var(--text-primary) !important;
            }
            
            .header {
                background: var(--bg-secondary) !important;
                color: var(--text-primary) !important;
            }
            
            .header h1, .header p {
                color: var(--text-primary) !important;
            }
            
            .executive-summary {
                background: var(--bg-accent) !important;
                color: var(--text-primary) !important;
                border: 2px solid var(--border-secondary) !important;
            }
            
            .metric-card {
                background: var(--bg-secondary) !important;
                color: var(--text-primary) !important;
                border: 1px solid var(--border-primary) !important;
            }
            
            .metric-label, .breakdown-label, .detail-label {
                color: var(--text-secondary) !important;
            }
            
            .metric-value, .breakdown-value, .detail-value {
                color: var(--text-primary) !important;
            }
            
            .eligibility-status.eligible {
                color: var(--accent-green) !important;
            }
            
            .eligibility-status.not-eligible {
                color: var(--accent-red) !important;
            }
            
            .footer {
                background: var(--bg-secondary) !important;
                color: var(--text-secondary) !important;
            }
        }
    </style>
</head>
<body>
    <div class="container">
        <div class="header">
            <h1>EquiForge™</h1>
            <p><strong>Professional Real Estate Report</strong></p>
            <p style="font-size: 1.1rem; margin: 15px 0;">Advanced Home Buying Intelligence for Real Estate Professionals</p>
            <p>Prepared for: <strong>${(() => {
                if (userInfo?.name) return userInfo.name;
                if (formData.personalDetails?.firstName && formData.personalDetails?.lastName) {
                  return `${formData.personalDetails.firstName} ${formData.personalDetails.lastName}`;
                }
                if (formData.personalDetails?.firstName) return formData.personalDetails.firstName;
                return 'EquiForge User';
              })()}</strong></p>
            ${userInfo?.email ? `<p>Email: ${userInfo.email}</p>` : ''}
            <p>Generated: ${new Date().toLocaleString()}</p>
            <p>Report ID: RPT-${Date.now()}</p>
        </div>

        <!-- Executive Summary -->
        <div class="executive-summary">
            <h2 style="margin-top: 0; color: white;">EXECUTIVE SUMMARY</h2>
            <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 20px;">
                <div>
                    <strong>Recommended Loan Program:</strong><br>
                    ${formData.propertyLoan?.loanType?.toUpperCase() || 'To Be Determined'}
                </div>
                <div>
                    <strong>Maximum Purchase Price:</strong><br>
                    ${formatCurrency(results?.maxPurchasePrice || 0)}
                </div>
                <div>
                    <strong>Monthly Payment Range:</strong><br>
                    ${formatCurrency(results?.keyMetrics?.monthlyPayment || 0)}
                </div>
                <div>
                    <strong>Required Down Payment:</strong><br>
                    ${formatCurrency(results?.keyMetrics?.cashToClose || 0)}
                </div>
                <div>
                    <strong>Approval Probability:</strong><br>
                    ${analytics?.riskAssessment?.riskLevel === 'Low' ? 'High (85-95%)' : analytics?.riskAssessment?.riskLevel === 'Medium' ? 'Moderate (70-84%)' : 'Requires Review (50-69%)'}
                </div>
            </div>
        </div>

        <!-- Key Metrics Grid -->
        <div class="key-metrics">
            <div class="metric-card monthly-payment">
                <div class="metric-label">Monthly Payment</div>
                <div class="metric-value">${formatCurrency(results?.keyMetrics?.monthlyPayment || 0)}</div>
            </div>
            <div class="metric-card total-interest">
                <div class="metric-label">Total Interest</div>
                <div class="metric-value">${formatCurrency(results?.keyMetrics?.totalInterest || 0)}</div>
            </div>
            <div class="metric-card closing-costs">
                <div class="metric-label">Net Closing Costs</div>
                <div class="metric-value">${formatCurrency(results?.keyMetrics?.netClosingCosts || 0)}</div>
            </div>
            <div class="metric-card cash-close">
                <div class="metric-label">Cash to Close</div>
                <div class="metric-value">${formatCurrency(results?.keyMetrics?.cashToClose || 0)}</div>
            </div>
            <div class="metric-card approval">
                <div class="metric-label">Approval Status</div>
                <div class="metric-value small">${analytics?.riskAssessment?.riskLevel === 'Low' ? 'EXCELLENT' : analytics?.riskAssessment?.riskLevel === 'Medium' ? 'GOOD' : 'REVIEW'}</div>
            </div>
        </div>

        <!-- Client Profile -->
        <div class="section">
            <h2>CLIENT PROFILE</h2>
            <div class="loan-details">
                ${userInfo?.name ? `
                <div class="detail-item">
                    <div class="detail-label">Client Name</div>
                    <div class="detail-value">${userInfo.name}</div>
                </div>
                ` : ''}
                ${userInfo?.email ? `
                <div class="detail-item">
                    <div class="detail-label">Contact</div>
                    <div class="detail-value">${userInfo.email}</div>
                </div>
                ` : ''}
                <div class="detail-item">
                    <div class="detail-label">Annual Income</div>
                    <div class="detail-value">${formatCurrency(formData.primaryIncome?.annualIncome || 0)}</div>
                </div>
                <div class="detail-item">
                    <div class="detail-label">Credit Score</div>
                    <div class="detail-value">${formData.personalDetails?.creditScore || 'Not provided'}</div>
                </div>
                <div class="detail-item">
                    <div class="detail-label">Property Type</div>
                    <div class="detail-value">${formData.personalDetails?.propertyType || 'Not specified'}</div>
                </div>
                <div class="detail-item">
                    <div class="detail-label">Location</div>
                    <div class="detail-value">${formData.personalDetails?.state || 'Not specified'}</div>
                </div>
                <div class="detail-item">
                    <div class="detail-label">First-Time Buyer</div>
                    <div class="detail-value">${formData.personalDetails?.isFirstTimeBuyer ? 'Yes' : 'No'}</div>
                </div>
                <div class="detail-item">
                    <div class="detail-label">Veteran Status</div>
                    <div class="detail-value">${isVeteran ? 'Yes' : 'No'}</div>
                </div>
            </div>
        </div>

        <!-- Financial Pre-Qualification Analysis -->
        <div class="section">
            <h2>FINANCIAL PRE-QUALIFICATION ANALYSIS</h2>
            
            <h3>Income Verification</h3>
            <div class="breakdown-item">
                <span class="breakdown-label">Primary Income Type</span>
                <span class="breakdown-value">${formData.primaryIncome?.incomeType === 'salary' ? 'W-2 Salary' : 'Hourly/Contract'}</span>
            </div>
            <div class="breakdown-item">
                <span class="breakdown-label">Annual Income</span>
                <span class="breakdown-value">${formatCurrency(formData.primaryIncome?.annualIncome || 0)}</span>
            </div>
            ${formData.primaryIncome?.additionalIncome ? `
            <div class="breakdown-item">
                <span class="breakdown-label">Additional Income</span>
                <span class="breakdown-value">${formatCurrency(formData.primaryIncome.additionalIncome)}</span>
            </div>
            ` : ''}
            ${formData.coBorrowerIncome?.enabled ? `
            <div class="breakdown-item">
                <span class="breakdown-label">Co-borrower Income</span>
                <span class="breakdown-value">${formatCurrency(formData.coBorrowerIncome.annualIncome || 0)}</span>
            </div>
            ` : ''}
            <div class="breakdown-item">
                <span class="breakdown-label">Monthly Income</span>
                <span class="breakdown-value">${formatCurrency((formData.primaryIncome?.annualIncome || 0) / 12)}</span>
            </div>
            <div class="breakdown-item">
                <span class="breakdown-label">Income Stability</span>
                <span class="breakdown-value">${formData.primaryIncome?.incomeType === 'salary' ? 'Stable (W-2)' : 'Variable (Requires 2yr history)'}</span>
            </div>

            <h3>Credit Profile</h3>
            ${(() => {
              const creditScore = formData.personalDetails?.creditScore || 650;
              const scoreRange = creditScore >= 740 ? 'Excellent (740+)' : creditScore >= 670 ? 'Good (670-739)' : creditScore >= 580 ? 'Fair (580-669)' : 'Poor (below 580)';
              return `
              <div class="breakdown-item">
                  <span class="breakdown-label">Current Score</span>
                  <span class="breakdown-value">${creditScore}</span>
              </div>
              <div class="breakdown-item">
                  <span class="breakdown-label">Score Range</span>
                  <span class="breakdown-value">${scoreRange}</span>
              </div>
              ${creditScore < 740 ? `
              <div style="margin: 20px 0; padding: 15px; background: hsl(240 5.9% 10%); border-radius: 8px;">
                  <h4 style="color: hsl(47.9 95.8% 53.1%); margin: 0 0 10px 0;">Credit Improvement Potential</h4>
                   <p style="margin: 5px 0; color: hsl(210 40% 98%);">Improvement Potential: ${740 - creditScore} points</p>
                   <strong style="color: hsl(210 40% 98%);">Recommended Actions:</strong>
                   <ul style="color: hsl(210 40% 98%); margin: 10px 0; padding-left: 20px;">
                      ${creditScore < 620 ? `
                      <li>Pay down credit card balances immediately</li>
                      <li>Dispute any credit report errors</li>
                      <li>Timeline: 3-6 months for significant improvement</li>
                      ` : creditScore < 680 ? `
                      <li>Make all payments on time</li>
                      <li>Avoid new credit inquiries</li>
                      <li>Timeline: 30-90 days for modest improvement</li>
                      ` : `
                      <li>Keep credit utilization below 10%</li>
                      <li>Maintain older credit accounts</li>
                      `}
                  </ul>
              </div>
              ` : ''}`;
            })()}

            <h3>Down Payment & Reserves</h3>
            ${(() => {
              const downPaymentAmount = formData.propertyLoan?.downPaymentAmount || 0;
              const downPaymentPercent = formData.propertyLoan?.downPaymentPercentage || 0;
              const loanType = formData.propertyLoan?.loanType || 'conventional';
              const requiredReserves = loanType === 'conventional' ? 2 : loanType === 'fha' ? 1 : loanType === 'va' ? 0 : 1;
              return `
              <div class="breakdown-item">
                  <span class="breakdown-label">Available Down Payment</span>
                  <span class="breakdown-value">${formatCurrency(downPaymentAmount)}</span>
              </div>
              <div class="breakdown-item">
                  <span class="breakdown-label">Down Payment Percentage</span>
                  <span class="breakdown-value">${downPaymentPercent}%</span>
              </div>
              <div class="breakdown-item">
                  <span class="breakdown-label">PMI Status</span>
                  <span class="breakdown-value">${downPaymentPercent >= 20 ? 'Not required (20%+ down)' : 'Required (less than 20% down)'}</span>
              </div>
              <div class="breakdown-item">
                  <span class="breakdown-label">Required Reserves</span>
                  <span class="breakdown-value">${requiredReserves} months PITI</span>
              </div>
              <div class="breakdown-item">
                  <span class="breakdown-label">Recommended Reserves</span>
                  <span class="breakdown-value">6 months PITI</span>
              </div>`;
            })()}
        </div>

        <!-- Loan Program Optimization Matrix -->
        <div class="section">
            <h2>LOAN PROGRAM OPTIMIZATION MATRIX</h2>
            
            ${(() => {
              const creditScore = formData.personalDetails?.creditScore || 650;
              let primaryProgram = 'Conventional 30-Year Fixed';
              if (formData.vaEligibility?.veteranStatus) primaryProgram = 'VA 30-Year Fixed';
              else if (formData.personalDetails?.isRuralArea) primaryProgram = 'USDA Rural Development';
              else if (creditScore < 650) primaryProgram = 'FHA 30-Year Fixed';
              
              return `
              <div style="background: hsl(221.2 83.2% 53.3% / 0.1); border: 2px solid hsl(221.2 83.2% 53.3%); border-radius: 12px; padding: 20px; margin: 20px 0;">
                  <h3 style="margin: 0 0 15px 0; color: hsl(221.2 83.2% 53.3%);">PRIMARY RECOMMENDATION: ${primaryProgram}</h3>
                  
                  ${primaryProgram.includes('VA') ? `
                  <div style="margin: 15px 0;">
                      <strong style="color: hsl(142.1 76.2% 36.3%);">Advantages:</strong>
                       <ul style="margin: 8px 0; padding-left: 20px; color: #000000;">
                          <li>No down payment required</li>
                          <li>No PMI required</li>
                          <li>Competitive interest rates</li>
                          <li>No prepayment penalties</li>
                      </ul>
                      <strong style="color: hsl(47.9 95.8% 53.1%);">Requirements:</strong>
                      <ul style="margin: 8px 0; padding-left: 20px; color: #000000;">
                          <li>Certificate of Eligibility (COE)</li>
                          <li>Primary residence only</li>
                          <li>VA property condition requirements</li>
                      </ul>
                      <strong>Processing Time:</strong> 30-45 days
                  </div>
                  ` : primaryProgram.includes('FHA') ? `
                  <div style="margin: 15px 0;">
                      <strong style="color: hsl(142.1 76.2% 36.3%);">Advantages:</strong>
                        <ul style="margin: 8px 0; padding-left: 20px; color: #000000;">
                           <li>Low down payment (3.5%)</li>
                           <li>Flexible credit requirements</li>
                           <li>Assumable loans</li>
                       </ul>
                       <strong style="color: hsl(47.9 95.8% 53.1%);">Requirements:</strong>
                        <ul style="margin: 8px 0; padding-left: 20px; color: #000000;">
                           <li>Minimum credit score 580</li>
                           <li>MIP (mortgage insurance) required</li>
                           <li>FHA property condition standards</li>
                       </ul>
                      <strong>Processing Time:</strong> 30-45 days
                  </div>
                  ` : primaryProgram.includes('USDA') ? `
                  <div style="margin: 15px 0;">
                      <strong style="color: hsl(142.1 76.2% 36.3%);">Advantages:</strong>
                        <ul style="margin: 8px 0; padding-left: 20px; color: #000000;">
                           <li>No down payment required</li>
                           <li>Below-market interest rates</li>
                           <li>Flexible credit requirements</li>
                       </ul>
                       <strong style="color: hsl(47.9 95.8% 53.1%);">Requirements:</strong>
                        <ul style="margin: 8px 0; padding-left: 20px; color: #000000;">
                           <li>Property in USDA-eligible rural area</li>
                           <li>Income limits apply</li>
                           <li>Primary residence only</li>
                       </ul>
                      <strong>Processing Time:</strong> 45-60 days
                  </div>
                  ` : `
                  <div style="margin: 15px 0;">
                      <strong style="color: hsl(142.1 76.2% 36.3%);">Advantages:</strong>
                        <ul style="margin: 8px 0; padding-left: 20px; color: #000000;">
                           <li>No mortgage insurance with 20% down</li>
                           <li>Competitive rates</li>
                           <li>No property restrictions</li>
                       </ul>
                       <strong style="color: hsl(47.9 95.8% 53.1%);">Requirements:</strong>
                       <ul style="margin: 8px 0; padding-left: 20px; color: #000000;">
                           <li>Minimum credit score 620</li>
                           <li>DTI ≤ 45% typically</li>
                           <li>5-20% down payment</li>
                      </ul>
                      <strong>Processing Time:</strong> 30-45 days
                  </div>
                  `}
              </div>`;
            })()}

            <!-- Loan Eligibility Matrix -->
            <h3>Loan Program Eligibility Status</h3>
            <div class="loan-eligibility">
                ${(() => {
                  const creditScore = formData.personalDetails?.creditScore || 650;
                  // Check veteran status from multiple sources for accuracy
                  const isVeteran = formData.vaEligibility?.veteranStatus || 
                                   (formData.personalDetails as any)?.veteranStatus === 'veteran' ||
                                   (formData.personalDetails as any)?.veteranStatus === 'activeService' ||
                                   formData.propertyLoan?.loanType === 'va';
                  
                  const programs = [
                    { name: 'Conventional', eligible: creditScore >= 620, reason: creditScore < 620 ? 'Credit score below 620' : '' },
                    { name: 'FHA', eligible: creditScore >= 580, reason: creditScore < 580 ? 'Credit score below 580' : '' },
                    { name: 'VA', eligible: isVeteran, reason: !isVeteran ? 'Not a veteran' : '' },
                    { name: 'USDA', eligible: formData.personalDetails?.isRuralArea, reason: !formData.personalDetails?.isRuralArea ? 'Property not in rural area' : '' }
                  ];
                  
                  return programs.map(program => `
                    <div class="eligibility-card ${program.eligible ? 'eligible' : 'not-eligible'}">
                        <div class="loan-program">${program.name}</div>
                        <div class="eligibility-status ${program.eligible ? 'eligible' : 'not-eligible'}">
                            ${program.eligible ? 'ELIGIBLE' : 'NOT ELIGIBLE'}
                        </div>
                        ${program.reason ? `<div style="font-size: 0.8rem; color: #000000; margin-top: 4px;">${program.reason}</div>` : ''}
                    </div>
                  `).join('');
                })()}
            </div>
        </div>

        <!-- Property Search Parameters -->
        <div class="section">
            <h2>PROPERTY SEARCH PARAMETERS</h2>
            ${(() => {
              const maxPrice = results?.maxPurchasePrice || formData.propertyLoan?.homePrice || 400000;
              const comfortableMax = maxPrice * 0.85;
              return `
              <h3>Price Ranges</h3>
              <div class="breakdown-item">
                  <span class="breakdown-label">Comfortable Range</span>
                  <span class="breakdown-value">${formatCurrency(comfortableMax * 0.7)} - ${formatCurrency(comfortableMax)}</span>
              </div>
              <div class="breakdown-item">
                  <span class="breakdown-label">Maximum Range</span>
                  <span class="breakdown-value">${formatCurrency(comfortableMax)} - ${formatCurrency(maxPrice)}</span>
              </div>
              <div class="breakdown-item">
                  <span class="breakdown-label">Buffer for Bidding</span>
                  <span class="breakdown-value">${formatCurrency(maxPrice * 0.05)}</span>
              </div>
              
              <h3>Property Criteria</h3>
              <div class="breakdown-item">
                  <span class="breakdown-label">Property Type</span>
                  <span class="breakdown-value">${formData.personalDetails?.propertyType || 'Any'}</span>
              </div>
              <div class="breakdown-item">
                  <span class="breakdown-label">Target Location</span>
                  <span class="breakdown-value">${formData.personalDetails?.state || 'Not specified'}</span>
              </div>
              `;
            })()}
        </div>
        
        <!-- Monthly Payment Breakdown -->
        ${results?.monthlyBreakdown ? `
        <div class="section">
            <h2>Monthly Payment Breakdown</h2>
            <div class="breakdown-item">
                <span class="breakdown-label">Principal & Interest</span>
                <span class="breakdown-value">${formatCurrency(results.monthlyBreakdown.principalAndInterest)}</span>
            </div>
            <div class="breakdown-item">
                <span class="breakdown-label">Property Taxes</span>
                <span class="breakdown-value">${formatCurrency(results.monthlyBreakdown.propertyTaxes)}</span>
            </div>
            <div class="breakdown-item">
                <span class="breakdown-label">Home Insurance</span>
                <span class="breakdown-value">${formatCurrency(results.monthlyBreakdown.homeInsurance)}</span>
            </div>
            ${results.monthlyBreakdown.pmi > 0 ? `
            <div class="breakdown-item">
                <span class="breakdown-label">PMI</span>
                <span class="breakdown-value">${formatCurrency(results.monthlyBreakdown.pmi)}</span>
            </div>
            ` : ''}
            ${results.monthlyBreakdown.hoaFees > 0 ? `
            <div class="breakdown-item">
                <span class="breakdown-label">HOA Fees</span>
                <span class="breakdown-value">${formatCurrency(results.monthlyBreakdown.hoaFees)}</span>
            </div>
            ` : ''}
        </div>
        ` : ''}

        <!-- Supreme Intelligence Analytics -->
        ${(() => {
          // Generate chart data if supremeIntelligence or mortgage data is available
          if (supremeIntelligence || (formData.propertyLoan?.homePrice && formData.propertyLoan?.downPaymentAmount && formData.propertyLoan?.interestRate)) {
            const mortgageData: MortgageInputs = {
              homePrice: supremeIntelligence?.homePrice || formData.propertyLoan?.homePrice || 400000,
              downPayment: supremeIntelligence?.downPayment || formData.propertyLoan?.downPaymentAmount || 80000,
              interestRate: supremeIntelligence?.interestRate || formData.propertyLoan?.interestRate || 6.5,
              homeInsurance: supremeIntelligence?.homeInsurance || 1200,
              propertyTax: supremeIntelligence?.propertyTax || 4800,
              closingCosts: { total: supremeIntelligence?.closingCosts?.total || 15000 }
            };
            
            const selectedTerm = supremeIntelligence?.selectedTerm || 30;
            const amortizationData = generateAmortizationChartData(mortgageData, selectedTerm);
            const costScenariosData = generateCostScenariosChartData(mortgageData, selectedTerm);
            const equityGrowthData = generateEquityGrowthChartData(mortgageData, selectedTerm);
            
            return `
            <div class="section">
                <h2>SUPREME INTELLIGENCE ANALYTICS</h2>
                <p style="color: var(--text-secondary); margin-bottom: 30px; font-style: italic;">
                    Advanced financial modeling and projections based on your specific loan parameters
                </p>
                
                <!-- Chart.js CDN with enhanced loading -->
                <script>
                    window.chartJsLoaded = false;
                    window.chartLoadError = false;
                </script>
                <script src="https://cdn.jsdelivr.net/npm/chart.js@4.4.0/dist/chart.umd.js" 
                        crossorigin="anonymous"
                        onload="window.chartJsLoaded = true; console.log('Chart.js loaded successfully');"
                        onerror="window.chartLoadError = true; console.error('Failed to load Chart.js');"></script>
                <style>
                    .chart-container {
                        position: relative;
                        height: 400px;
                        width: 100%;
                        background: rgba(15, 23, 42, 0.5);
                        border-radius: 8px;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                    }
                    .chart-loading {
                        color: hsl(215.4 16.3% 56.9%);
                        text-align: center;
                    }
                    .chart-canvas {
                        display: block;
                        width: 100% !important;
                        height: 100% !important;
                        background: transparent;
                    }
                </style>
                
                <!-- Amortization Analysis -->
                <div style="background: hsl(215 27.9% 16.9%); border: 1px solid hsl(215 13.8% 34%); border-radius: 12px; padding: 24px; margin: 30px 0;">
                    <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px;">
                        <h3 style="color: hsl(210 40% 98%); margin: 0; font-size: 1.2em;">Principal vs Interest Over Time</h3>
                        <span style="color: hsl(215.4 16.3% 56.9%); font-size: 0.9em;">Term: ${selectedTerm} Years</span>
                    </div>
                    <div class="chart-container" id="amortizationChartContainer">
                        <div class="chart-loading" id="amortizationLoading">
                            <div style="margin-bottom: 10px;">Loading Interactive Chart...</div>
                        </div>
                        <canvas id="amortizationChart" class="chart-canvas" style="display: none;" width="800" height="400"></canvas>
                    </div>
                    <div style="display: flex; gap: 20px; margin-top: 15px; font-size: 0.9em;">
                        <div style="display: flex; align-items: center; gap: 8px;">
                            <div style="width: 16px; height: 16px; background: #ef4444; border-radius: 2px;"></div>
                            <span style="color: hsl(215.4 16.3% 56.9%);">Interest</span>
                        </div>
                        <div style="display: flex; align-items: center; gap: 8px;">
                            <div style="width: 16px; height: 16px; background: #2563eb; border-radius: 2px;"></div>
                            <span style="color: hsl(215.4 16.3% 56.9%);">Principal</span>
                        </div>
                    </div>
                </div>
                
                <!-- Cost Scenarios Comparison -->
                <div style="background: hsl(215 27.9% 16.9%); border: 1px solid hsl(215 13.8% 34%); border-radius: 12px; padding: 24px; margin: 30px 0;">
                    <h3 style="color: hsl(210 40% 98%); margin: 0 0 20px 0; font-size: 1.2em;">Cost Comparison Scenarios (${selectedTerm} Year Base Term)</h3>
                    <div class="chart-container" id="costScenariosChartContainer">
                        <div class="chart-loading" id="costScenariosLoading">
                            <div style="margin-bottom: 10px;">Loading Interactive Chart...</div>
                        </div>
                        <canvas id="costScenariosChart" class="chart-canvas" style="display: none;" width="800" height="400"></canvas>
                    </div>
                </div>
                
                <!-- Equity Growth Projection -->
                <div style="background: hsl(215 27.9% 16.9%); border: 1px solid hsl(215 13.8% 34%); border-radius: 12px; padding: 24px; margin: 30px 0;">
                    <h3 style="color: hsl(210 40% 98%); margin: 0 0 20px 0; font-size: 1.2em;">Equity Growth Projection (${selectedTerm} Year Term)</h3>
                    <div class="chart-container" id="equityGrowthChartContainer">
                        <div class="chart-loading" id="equityGrowthLoading">
                            <div style="margin-bottom: 10px;">Loading Interactive Chart...</div>
                        </div>
                        <canvas id="equityGrowthChart" class="chart-canvas" style="display: none;" width="800" height="400"></canvas>
                    </div>
                    <div style="display: flex; gap: 20px; margin-top: 15px; font-size: 0.9em;">
                        <div style="display: flex; align-items: center; gap: 8px;">
                            <div style="width: 16px; height: 16px; background: #8b5cf6; border-radius: 2px;"></div>
                            <span style="color: hsl(215.4 16.3% 56.9%);">Home Value</span>
                        </div>
                        <div style="display: flex; align-items: center; gap: 8px;">
                            <div style="width: 16px; height: 16px; background: #ef4444; border-radius: 2px; border: 1px dashed #ef4444;"></div>
                            <span style="color: hsl(215.4 16.3% 56.9%);">Loan Balance</span>
                        </div>
                        <div style="display: flex; align-items: center; gap: 8px;">
                            <div style="width: 16px; height: 16px; background: #10b981; border-radius: 2px;"></div>
                            <span style="color: hsl(215.4 16.3% 56.9%);">Equity</span>
                        </div>
                    </div>
                </div>
                
                <!-- JavaScript for Chart Generation -->
                <script>
                    let chartInitAttempts = 0;
                    const maxAttempts = 50;
                    
                    function showChartFallback(chartId, message) {
                        const container = document.getElementById(chartId + 'Container');
                        const loading = document.getElementById(chartId.replace('Chart', 'Loading'));
                        if (container && loading) {
                            // Use textContent instead of innerHTML to prevent XSS
                            const errorDiv = document.createElement('div');
                            errorDiv.style.color = '#ef4444';
                            errorDiv.textContent = 'Chart could not load. ' + message;
                            loading.innerHTML = '';
                            loading.appendChild(errorDiv);
                        }
                    }
                    
                    function hideLoading(chartId) {
                        const loading = document.getElementById(chartId.replace('Chart', 'Loading'));
                        const canvas = document.getElementById(chartId);
                        if (loading && canvas) {
                            loading.style.display = 'none';
                            canvas.style.display = 'block';
                        }
                    }
                    
                    function initializeCharts() {
                        chartInitAttempts++;
                        console.log('Chart initialization attempt:', chartInitAttempts);
                        
                        // Check for Chart.js or loading error
                        if (window.chartLoadError) {
                            console.error('Chart.js failed to load from CDN');
                            showChartFallback('amortizationChart', 'Chart.js library failed to load from CDN.');
                            showChartFallback('costScenariosChart', 'Chart.js library failed to load from CDN.');
                            showChartFallback('equityGrowthChart', 'Chart.js library failed to load from CDN.');
                            return;
                        }
                        
                        if (typeof Chart === 'undefined' && !window.chartJsLoaded) {
                            if (chartInitAttempts < maxAttempts) {
                                console.log('Chart.js not loaded yet, retrying in 200ms...');
                                setTimeout(initializeCharts, 200);
                            } else {
                                console.error('Chart.js failed to load after', maxAttempts, 'attempts');
                                showChartFallback('amortizationChart', 'Chart.js library failed to load after multiple attempts.');
                                showChartFallback('costScenariosChart', 'Chart.js library failed to load after multiple attempts.');
                                showChartFallback('equityGrowthChart', 'Chart.js library failed to load after multiple attempts.');
                            }
                            return;
                        }
                        
                        console.log('Chart.js loaded successfully, creating charts...');
                        
                        try {
                            
                            // Amortization Chart
                            const amortizationCtx = document.getElementById('amortizationChart');
                            if (amortizationCtx && amortizationCtx.getContext) {
                                const ctx = amortizationCtx.getContext('2d');
                                if (ctx) {
                                    new Chart(ctx, {
                                    type: 'bar',
                                    data: {
                                        labels: ${JSON.stringify(amortizationData.map(d => d.year))},
                                        datasets: [{
                                            label: 'Interest',
                                            data: ${JSON.stringify(amortizationData.map(d => d.interest))},
                                            backgroundColor: '#ef4444',
                                            stack: 'Stack 0'
                                        }, {
                                            label: 'Principal',
                                            data: ${JSON.stringify(amortizationData.map(d => d.principal))},
                                            backgroundColor: '#2563eb',
                                            stack: 'Stack 0'
                                        }]
                                    },
                                    options: {
                                        responsive: true,
                                        maintainAspectRatio: false,
                                        plugins: {
                                            legend: { display: false },
                                            tooltip: {
                                                backgroundColor: '#0f172a',
                                                titleColor: '#e2e8f0',
                                                bodyColor: '#e2e8f0',
                                                borderColor: '#334155',
                                                borderWidth: 1,
                                                callbacks: {
                                                    label: function(context) {
                                                        return context.dataset.label + ': $' + context.parsed.y.toLocaleString();
                                                    }
                                                }
                                            }
                                        },
                                        scales: {
                                            x: {
                                                ticks: { color: '#94a3b8', maxRotation: 45 },
                                                grid: { display: false }
                                            },
                                            y: {
                                                ticks: { 
                                                    color: '#94a3b8',
                                                    callback: function(value) {
                                                        return '$' + (value / 1000) + 'k';
                                                    }
                                                },
                                                grid: { color: '#334155' }
                                            }
                                        }
                                    }
                                    });
                                    hideLoading('amortizationChart');
                                    console.log('Amortization chart created successfully');
                                } else {
                                    console.error('Failed to get 2D context for amortization chart');
                                    showChartFallback('amortizationChart', 'Canvas context error.');
                                }
                            } else {
                                console.error('Amortization chart canvas not found');
                                showChartFallback('amortizationChart', 'Canvas element not found.');
                            }
                            
                            // Cost Scenarios Chart  
                            const costScenariosCtx = document.getElementById('costScenariosChart');
                            if (costScenariosCtx && costScenariosCtx.getContext) {
                                const ctx = costScenariosCtx.getContext('2d');
                                if (ctx) {
                                    new Chart(ctx, {
                                    type: 'bar',
                                    data: {
                                        labels: ${JSON.stringify(costScenariosData.map(d => d.name))},
                                        datasets: [{
                                            label: 'Total Cost',
                                            data: ${JSON.stringify(costScenariosData.map(d => d.totalCost))},
                                            backgroundColor: '#f59e0b'
                                        }]
                                    },
                                    options: {
                                        responsive: true,
                                        maintainAspectRatio: false,
                                        plugins: {
                                            legend: { display: false },
                                            tooltip: {
                                                backgroundColor: '#0f172a',
                                                titleColor: '#e2e8f0',
                                                bodyColor: '#e2e8f0',
                                                borderColor: '#334155',
                                                borderWidth: 1,
                                                callbacks: {
                                                    label: function(context) {
                                                        return 'Total Cost: $' + context.parsed.y.toLocaleString();
                                                    }
                                                }
                                            }
                                        },
                                        scales: {
                                            x: {
                                                ticks: { color: '#94a3b8' },
                                                grid: { display: false }
                                            },
                                            y: {
                                                ticks: { 
                                                    color: '#94a3b8',
                                                    callback: function(value) {
                                                        return '$' + (value / 1000) + 'k';
                                                    }
                                                },
                                                grid: { color: '#334155' }
                                            }
                                        }
                                    }
                                    });
                                    hideLoading('costScenariosChart');
                                    console.log('Cost scenarios chart created successfully');
                                } else {
                                    console.error('Failed to get 2D context for cost scenarios chart');
                                    showChartFallback('costScenariosChart', 'Canvas context error.');
                                }
                            } else {
                                console.error('Cost scenarios chart canvas not found');
                                showChartFallback('costScenariosChart', 'Canvas element not found.');
                            }
                            
                            // Equity Growth Chart
                            const equityGrowthCtx = document.getElementById('equityGrowthChart');
                            if (equityGrowthCtx && equityGrowthCtx.getContext) {
                                const ctx = equityGrowthCtx.getContext('2d');
                                if (ctx) {
                                    new Chart(ctx, {
                                    type: 'line',
                                    data: {
                                        labels: ${JSON.stringify(equityGrowthData.map(d => d.year))},
                                        datasets: [{
                                            label: 'Home Value',
                                            data: ${JSON.stringify(equityGrowthData.map(d => d.homeValue))},
                                            borderColor: '#8b5cf6',
                                            backgroundColor: '#8b5cf6',
                                            borderWidth: 3,
                                            fill: false,
                                            pointRadius: 4,
                                            pointHoverRadius: 6
                                        }, {
                                            label: 'Loan Balance',
                                            data: ${JSON.stringify(equityGrowthData.map(d => d.loanBalance))},
                                            borderColor: '#ef4444',
                                            backgroundColor: '#ef4444',
                                            borderWidth: 3,
                                            borderDash: [5, 5],
                                            fill: false,
                                            pointRadius: 4,
                                            pointHoverRadius: 6
                                        }, {
                                            label: 'Equity',
                                            data: ${JSON.stringify(equityGrowthData.map(d => d.equity))},
                                            borderColor: '#10b981',
                                            backgroundColor: '#10b981',
                                            borderWidth: 3,
                                            fill: false,
                                            pointRadius: 4,
                                            pointHoverRadius: 6
                                        }]
                                    },
                                    options: {
                                        responsive: true,
                                        maintainAspectRatio: false,
                                        plugins: {
                                            legend: { display: false },
                                            tooltip: {
                                                backgroundColor: '#0f172a',
                                                titleColor: '#e2e8f0',
                                                bodyColor: '#e2e8f0',
                                                borderColor: '#334155',
                                                borderWidth: 1,
                                                callbacks: {
                                                    label: function(context) {
                                                        const label = context.dataset.label === 'homeValue' ? 'Home Value' : 
                                                                     context.dataset.label === 'loanBalance' ? 'Loan Balance' : 'Equity';
                                                        return label + ': $' + context.parsed.y.toLocaleString();
                                                    }
                                                }
                                            }
                                        },
                                        scales: {
                                            x: {
                                                ticks: { color: '#94a3b8' },
                                                grid: { display: false }
                                            },
                                            y: {
                                                ticks: { 
                                                    color: '#94a3b8',
                                                    callback: function(value) {
                                                        return '$' + (value / 1000) + 'k';
                                                    }
                                                },
                                                grid: { color: '#334155' }
                                            }
                                        }
                                    }
                                    });
                                    hideLoading('equityGrowthChart');
                                    console.log('Equity growth chart created successfully');
                                } else {
                                    console.error('Failed to get 2D context for equity growth chart');
                                    showChartFallback('equityGrowthChart', 'Canvas context error.');
                                }
                            } else {
                                console.error('Equity growth chart canvas not found');
                                showChartFallback('equityGrowthChart', 'Canvas element not found.');
                            }
                            
                        } catch (error) {
                            console.error('Error creating charts:', error);
                        }
                    }
                    
                    // Wait for Chart.js to be ready
                    function waitForChart() {
                        if (window.chartJsLoaded || typeof Chart !== 'undefined') {
                            initializeCharts();
                        } else if (window.chartLoadError) {
                            console.error('Chart.js failed to load, showing fallbacks');
                            showChartFallback('amortizationChart', 'Chart.js library failed to load.');
                            showChartFallback('costScenariosChart', 'Chart.js library failed to load.');
                            showChartFallback('equityGrowthChart', 'Chart.js library failed to load.');
                        } else {
                            setTimeout(waitForChart, 100);
                        }
                    }
                    
                    // Multiple initialization strategies
                    if (document.readyState === 'loading') {
                        document.addEventListener('DOMContentLoaded', waitForChart);
                    } else {
                        waitForChart();
                    }
                    
                    // Fallback with window load
                    window.addEventListener('load', waitForChart);
                </script>
            </div>
            `;
          }
          return '';
        })()}

        <!-- Market Intelligence & Strategy -->
        <div class="section">
            <h2>MARKET INTELLIGENCE & STRATEGY</h2>
            
            <h3>Market Positioning</h3>
            <div style="background: var(--bg-accent); border: 1px solid var(--border); padding: 15px; border-radius: 8px; margin: 15px 0;">
                 <p style="color: var(--text-primary); margin: 8px 0;">
                    <strong style="color: var(--accent-blue);">Price Range Strategy:</strong> 
                    Focus search within ${formatCurrency((results?.maxPurchasePrice || 400000) * 0.7)} - ${formatCurrency((results?.maxPurchasePrice || 400000) * 0.85)} for optimal negotiating position.
                </p>
                 <p style="color: var(--text-primary); margin: 8px 0;">
                    <strong style="color: var(--accent-blue);">Competitive Edge:</strong> 
                    ${formData.vaEligibility?.veteranStatus ? 'VA loan advantage - no down payment required gives strong offer position.' : 
                      formData.personalDetails?.creditScore >= 740 ? 'Excellent credit enables competitive rate negotiations.' :
                      'Consider credit improvement for better terms and expanded options.'}
                </p>
                 <p style="color: var(--text-primary); margin: 8px 0;">
                    <strong style="color: var(--accent-blue);">Timeline:</strong> 
                    Pre-approval valid for 60-90 days. Begin property search immediately.
                </p>
            </div>

            <h3>Risk Assessment & Mitigation</h3>
            <div style="background: var(--bg-accent); border: 1px solid var(--border); padding: 15px; border-radius: 8px; margin: 15px 0;">
                ${(() => {
                  const riskLevel = analytics?.riskAssessment?.riskLevel || 'Medium';
                  const riskColor = riskLevel === 'Low' ? 'hsl(142.1 76.2% 36.3%)' : riskLevel === 'Medium' ? 'hsl(47.9 95.8% 53.1%)' : 'hsl(0 84.2% 60.2%)';
                  return `
                  <div style="display: flex; align-items: center; margin-bottom: 15px;">
                      <span style="color: ${riskColor}; font-weight: bold; font-size: 1.1rem;">Risk Level: ${riskLevel}</span>
                  </div>
                   <ul style="color: var(--text-primary); margin: 8px 0; padding-left: 20px;">
                      ${riskLevel === 'Low' ? `
                      <li>Strong financial profile with minimal risk factors</li>
                      <li>Excellent approval probability across multiple loan programs</li>
                      <li>Competitive interest rate positioning</li>
                      ` : riskLevel === 'Medium' ? `
                      <li>Solid financial foundation with minor areas for improvement</li>
                      <li>Good approval chances with proper documentation</li>
                      <li>Consider rate lock timing for optimal terms</li>
                      ` : `
                      <li>Requires additional documentation and verification</li>
                      <li>Consider credit improvement strategies before application</li>
                      <li>Work with experienced loan officer for best outcomes</li>
                      `}
                   </ul>`;
                })()}
            </div>
        </div>

        <!-- Immediate Action Plan -->
        <div class="section">
            <h2>IMMEDIATE ACTION PLAN</h2>
            
            <h3>Next 7 Days</h3>
            <div style="background: var(--bg-accent); border: 1px solid var(--border); padding: 15px; border-radius: 8px; margin: 15px 0;">
                 <ul style="color: var(--text-primary); margin: 8px 0; padding-left: 20px;">
                    <li>Connect with recommended loan officer for pre-approval</li>
                    <li>Gather required documentation (pay stubs, tax returns, bank statements)</li>
                    <li>${formData.vaEligibility?.veteranStatus ? 'Obtain Certificate of Eligibility (COE) from VA' : 'Review credit report for accuracy'}</li>
                    <li>Research target neighborhoods and property values</li>
                </ul>
            </div>
            
            <h3>Next 30 Days</h3>
            <div style="background: var(--bg-accent); border: 1px solid var(--border); padding: 15px; border-radius: 8px; margin: 15px 0;">
                <ul style="color: var(--text-primary); margin: 8px 0; padding-left: 20px;">
                    <li>Complete formal loan application and underwriting</li>
                    <li>Begin active property search within recommended price range</li>
                    <li>Interview and select buyer's agent</li>
                    <li>Prepare for home inspections and appraisals</li>
                    <li>Set up automatic mortgage payment and escrow accounts</li>
                </ul>
            </div>

            <h3>Key Milestones</h3>
            <div class="breakdown-item">
                <span class="breakdown-label">Pre-approval Target</span>
                <span class="breakdown-value">7-14 days</span>
            </div>
            <div class="breakdown-item">
                <span class="breakdown-label">Property Search Phase</span>
                <span class="breakdown-value">30-60 days</span>
            </div>
            <div class="breakdown-item">
                <span class="breakdown-label">Closing Timeline</span>
                <span class="breakdown-value">30-45 days from offer acceptance</span>
            </div>
        </div>

        <!-- Professional Network & Referrals -->
        <div class="section">
            <h2>PROFESSIONAL NETWORK & REFERRALS</h2>
            
            <h3>Recommended Service Providers</h3>
            <div style="background: var(--bg-accent); border: 1px solid var(--border); padding: 15px; border-radius: 8px; margin: 15px 0;">
                <div style="margin-bottom: 15px;">
                    <strong style="color: var(--accent-blue);">Loan Officers:</strong>
                    <p style="color: var(--text-primary); margin: 5px 0;">Connect with EquiForge-verified loan officers specializing in ${formData.propertyLoan?.loanType || 'conventional'} loans</p>
                </div>
                <div style="margin-bottom: 15px;">
                    <strong style="color: var(--accent-blue);">Real Estate Agents:</strong>
                    <p style="color: var(--text-primary); margin: 5px 0;">Buyer's agents with expertise in ${formData.personalDetails?.state || 'your target'} market</p>
                </div>
                <div style="margin-bottom: 15px;">
                    <strong style="color: var(--accent-blue);">Home Inspectors:</strong>
                    <p style="color: var(--text-primary); margin: 5px 0;">Licensed inspectors familiar with ${formData.personalDetails?.propertyType || 'your property type'} requirements</p>
                </div>
                <div style="margin-bottom: 15px;">
                    <strong style="color: var(--accent-blue);">Title Companies:</strong>
                    <p style="color: var(--text-primary); margin: 5px 0;">Title services with competitive rates and fast processing</p>
                </div>
            </div>

            <h3>Contact Information</h3>
            <div style="background: hsl(47.9 95.8% 53.1% / 0.1); border: 1px solid hsl(47.9 95.8% 53.1%); border-radius: 8px; padding: 15px; margin: 15px 0;">
                <p style="color: var(--text-primary); margin: 8px 0;">
                    <strong style="color: var(--accent-blue);">For immediate assistance:</strong><br>
                    Contact your EquiForge representative or visit our platform for direct connections to verified professionals.
                </p>
            </div>
        </div>

        <!-- Important Disclaimers -->
        <div class="section">
            <h2>IMPORTANT DISCLAIMERS</h2>
            <div style="background: hsl(0 84.2% 60.2% / 0.1); border: 1px solid hsl(0 84.2% 60.2%); border-radius: 8px; padding: 20px; margin: 15px 0;">
                <ul style="color: var(--text-primary); margin: 8px 0; padding-left: 20px; line-height: 1.8;">
                    <li><strong>Estimates Only:</strong> All calculations are estimates based on provided information and current market conditions.</li>
                    <li><strong>Not a Loan Commitment:</strong> This report does not constitute a loan approval or commitment to lend.</li>
                    <li><strong>Rate Volatility:</strong> Interest rates change daily and final rates may differ from estimates.</li>
                    <li><strong>Professional Consultation:</strong> Always consult with qualified mortgage and real estate professionals.</li>
                    <li><strong>Market Conditions:</strong> Property values and market conditions may affect final terms and availability.</li>
                    <li><strong>Documentation Required:</strong> Loan approval subject to income, asset, and credit verification.</li>
                </ul>
            </div>
        </div>
        
        <!-- Loan Analysis -->
        ${results?.loanAnalysis ? `
        <div class="section">
            <h2>Loan Analysis</h2>
            <div class="loan-details">
                <div class="detail-item">
                    <div class="detail-label">Home Price</div>
                    <div class="detail-value">${formatCurrency(formData.propertyDetails?.homePrice || 0)}</div>
                </div>
                <div class="detail-item">
                    <div class="detail-label">Loan Amount</div>
                    <div class="detail-value">${formatCurrency(results.loanAnalysis.loanAmount)}</div>
                </div>
                <div class="detail-item">
                    <div class="detail-label">Loan Type</div>
                    <div class="detail-value">${results.loanAnalysis.loanType?.toUpperCase() || 'Not specified'}</div>
                </div>
                <div class="detail-item">
                    <div class="detail-label">Interest Rate</div>
                    <div class="detail-value">${formatPercent(results.loanAnalysis.interestRate || 0)}</div>
                </div>
                <div class="detail-item">
                    <div class="detail-label">Loan Term</div>
                    <div class="detail-value">${results.loanAnalysis.loanTerm || 30} years</div>
                </div>
                <div class="detail-item">
                    <div class="detail-label">Loan-to-Value</div>
                    <div class="detail-value">${formatPercent(results.loanAnalysis.loanToValue || 0)}</div>
                </div>
            </div>
        </div>
        ` : ''}

        <!-- Analytics Dashboard -->
        ${analytics ? `
        <div class="section">
            <h2>Analytics Dashboard</h2>
            
            ${analytics.riskAssessment ? `
            <div class="section">
                <h2>Risk Assessment</h2>
                <div class="loan-details">
                    <div class="detail-item">
                        <div class="detail-label">Risk Level</div>
                        <div class="detail-value">${analytics.riskAssessment.riskLevel}</div>
                    </div>
                    <div class="detail-item">
                        <div class="detail-label">Loan-to-Value</div>
                        <div class="detail-value">${formatPercent(analytics.riskAssessment.loanToValue)}</div>
                    </div>
                    <div class="detail-item">
                        <div class="detail-label">Down Payment Ratio</div>
                        <div class="detail-value">${formatPercent(analytics.riskAssessment.downPaymentRatio)}</div>
                    </div>
                    <div class="detail-item">
                        <div class="detail-label">Closing Cost Ratio</div>
                        <div class="detail-value">${formatPercent(analytics.riskAssessment.closingCostRatio)}</div>
                    </div>
                </div>
            </div>
            ` : ''}
            
            ${analytics.costAnalysis ? `
            <div class="section">
                <h2>Cost Analysis</h2>
                <div class="breakdown-item">
                    <span class="breakdown-label">Total Cost of Ownership</span>
                    <span class="breakdown-value">${formatCurrency(analytics.costAnalysis.totalCostOfOwnership)}</span>
                </div>
                <div class="breakdown-item">
                    <span class="breakdown-label">Interest to Loan Ratio</span>
                    <span class="breakdown-value">${formatPercent(analytics.costAnalysis.interestToLoanRatio)}</span>
                </div>
            </div>
            ` : ''}
            
            ${analytics.incomeRequirements ? `
            <div class="section">
                <h2>Income Requirements</h2>
                <div class="breakdown-item">
                    <span class="breakdown-label">Recommended Annual Income</span>
                    <span class="breakdown-value">${formatCurrency(analytics.incomeRequirements.recommendedAnnualIncome)}</span>
                </div>
                <div class="breakdown-item">
                    <span class="breakdown-label">Recommended Monthly Income</span>
                    <span class="breakdown-value">${formatCurrency(analytics.incomeRequirements.recommendedMonthlyIncome)}</span>
                </div>
                <div class="breakdown-item">
                    <span class="breakdown-label">Target DTI Ratio</span>
                    <span class="breakdown-value">${formatPercent(analytics.incomeRequirements.debtToIncomeRatio)}</span>
                </div>
            </div>
            ` : ''}
            
            ${analytics.keyInsights?.length > 0 ? `
            <div class="section">
                <h2>Key Insights</h2>
                ${analytics.keyInsights.map((insight: any) => {
                  const statusColor = insight.status === 'good' ? 'hsl(142.1 76.2% 36.3%)' : 
                                    insight.status === 'warning' ? 'hsl(47.9 95.8% 53.1%)' : 'hsl(0 84.2% 60.2%)';
                  return `
                  <div style="padding: 16px; margin: 12px 0; border-left: 4px solid ${statusColor}; background: hsl(240 5.9% 10%); border-radius: 8px;">
                      <strong style="color: #ffffff;">${insight.title}</strong><br>
                      <span style="color: #ffffff;">${insight.message}</span>
                  </div>`;
                }).join('')}
            </div>
            ` : ''}
        </div>
        ` : ''}

        <!-- Supreme Intelligence Analysis -->
        ${supremeIntelligence ? `
        <div class="section">
            <h2>Supreme Intelligence Analysis</h2>
            
            ${supremeIntelligence.availablePrograms?.length > 0 ? `
            <div class="section">
                <h2>Available Loan Programs</h2>
                ${supremeIntelligence.availablePrograms.map((program: any) => {
                  const borderColor = program.recommended ? 'hsl(142.1 76.2% 36.3%)' : 'hsl(240 3.7% 15.9%)';
                  return `
                  <div style="padding: 20px; margin: 16px 0; border: 2px solid ${borderColor}; border-radius: 12px; background: hsl(240 5.9% 10%);">
                      <h3 style="margin: 0 0 12px 0; color: hsl(210 40% 98%);">${program.name}${program.recommended ? ' (RECOMMENDED)' : ''}</h3>
                      <div class="loan-details">
                          <div class="detail-item">
                              <div class="detail-label">Monthly Payment</div>
                              <div class="detail-value">${formatCurrency(program.monthlyPayment)}</div>
                          </div>
                          <div class="detail-item">
                              <div class="detail-label">Down Payment</div>
                              <div class="detail-value">${formatCurrency(program.downPayment)} (${formatPercent(program.downPercent)})</div>
                          </div>
                          <div class="detail-item">
                              <div class="detail-label">APR</div>
                              <div class="detail-value">${formatPercent(program.apr)}</div>
                          </div>
                          <div class="detail-item">
                              <div class="detail-label">Total Cost</div>
                              <div class="detail-value">${formatCurrency(program.totalCost)}</div>
                          </div>
                          ${program.savings > 0 ? `
                          <div class="detail-item">
                              <div class="detail-label">Potential Savings</div>
                              <div class="detail-value">${formatCurrency(program.savings)}</div>
                          </div>
                          ` : ''}
                          <div class="detail-item">
                              <div class="detail-label">Eligible</div>
                              <div class="detail-value">${program.eligible ? 'YES' : 'NO'}</div>
                          </div>
                      </div>
                  </div>`;
                }).join('')}
            </div>
            ` : ''}
            
            ${supremeIntelligence.optimization ? `
            <div class="section">
                <h2>Optimization Opportunities</h2>
                ${supremeIntelligence.optimization.extraPrincipalSavings > 0 ? `
                <div class="breakdown-item">
                    <span class="breakdown-label">Extra Principal Savings</span>
                    <span class="breakdown-value">${formatCurrency(supremeIntelligence.optimization.extraPrincipalSavings)}</span>
                </div>
                ` : ''}
                ${supremeIntelligence.optimization.refinancingOpportunity > 0 ? `
                <div class="breakdown-item">
                    <span class="breakdown-label">Refinancing Opportunity</span>
                    <span class="breakdown-value">${formatCurrency(supremeIntelligence.optimization.refinancingOpportunity)}/month</span>
                </div>
                ` : ''}
                
                ${supremeIntelligence.optimization.shortTermStrategies?.length > 0 ? `
                <div style="margin: 20px 0;">
                    <h3 style="color: hsl(210 40% 98%); margin-bottom: 12px;">Short-term Strategies</h3>
                     <ul style="color: var(--text-primary); padding-left: 20px;">
                        ${supremeIntelligence.optimization.shortTermStrategies.map((strategy: string) => 
                          `<li style="margin: 8px 0;">${strategy}</li>`
                        ).join('')}
                    </ul>
                </div>
                ` : ''}
                
                ${supremeIntelligence.optimization.longTermStrategies?.length > 0 ? `
                <div style="margin: 20px 0;">
                    <h3 style="color: hsl(210 40% 98%); margin-bottom: 12px;">Long-term Strategies</h3>
                    <ul style="color: var(--text-primary); padding-left: 20px;">
                        ${supremeIntelligence.optimization.longTermStrategies.map((strategy: string) => 
                          `<li style="margin: 8px 0;">${strategy}</li>`
                        ).join('')}
                    </ul>
                </div>
                ` : ''}
            </div>
            ` : ''}
        </div>
        ` : ''}

        ${eligibilityResults ? `
        <div class="section">
            <h2>Loan Program Eligibility</h2>
            ${Object.entries(eligibilityResults).map(([program, eligibility]) => {
              const eligible = (eligibility as any)?.eligible;
              const reason = (eligibility as any)?.reason || '';
              const statusColor = eligible ? 'hsl(142.1 76.2% 36.3%)' : 'hsl(0 84.2% 60.2%)';
              return `
              <div style="padding: 16px; margin: 12px 0; border-left: 4px solid ${statusColor}; background: hsl(240 5.9% 10%); border-radius: 8px;">
                  <strong>${program.toUpperCase()}:</strong> ${eligible ? 'ELIGIBLE' : 'NOT ELIGIBLE'}
                  ${reason ? `<br><small style="color: #000000;">${reason}</small>` : ''}
              </div>`;
            }).join('')}
        </div>
        ` : ''}

        ${comparisonResults && comparisonResults.length > 0 ? `
        <div class="section">
            <h2>Loan Comparison</h2>
            ${comparisonResults.map((loan: any, index: number) => {
              const borderColor = loan.recommended ? 'hsl(142.1 76.2% 36.3%)' : 'hsl(240 3.7% 15.9%)';
              return `
              <div style="padding: 20px; margin: 16px 0; border: 2px solid ${borderColor}; border-radius: 12px; background: var(--bg-accent);">
                  <h3 style="margin: 0 0 12px 0; color: var(--text-primary);">Option ${index + 1} - ${loan.loanType?.toUpperCase()}${loan.recommended ? ' (RECOMMENDED)' : ''}</h3>
                  <div class="breakdown-item">
                      <span class="breakdown-label">Max Purchase Price</span>
                      <span class="breakdown-value">${formatCurrency(loan.maxPurchasePrice || 0)}</span>
                  </div>
                  <div class="breakdown-item">
                      <span class="breakdown-label">Monthly Payment</span>
                      <span class="breakdown-value">${formatCurrency(loan.monthlyPayment || 0)}</span>
                  </div>
                  <div class="breakdown-item">
                      <span class="breakdown-label">Total Cost (30yr)</span>
                      <span class="breakdown-value">${formatCurrency(loan.totalCost || 0)}</span>
                  </div>
              </div>`;
            }).join('')}
        </div>
        ` : ''}

        <div class="footer">
            <h3 style="color: hsl(210 40% 98%); margin-bottom: 15px;">Report Generated by EquiForge™</h3>
            <p style="margin: 8px 0;"><strong>Advanced Mortgage Intelligence Platform</strong></p>
            <p style="margin: 8px 0;">Professional Real Estate Report - ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()}</p>
            <p style="margin: 8px 0; font-size: 0.9rem;">For questions, support, or to connect with verified mortgage professionals, visit your EquiForge dashboard.</p>
            <div style="margin-top: 20px; padding-top: 15px; border-top: 1px solid hsl(240 3.7% 15.9%);">
                 <p style="font-size: 0.85rem; color: var(--text-muted); margin: 4px 0;">
                    This comprehensive analysis is provided for informational purposes. Loan terms subject to underwriting approval.
                </p>
                <p style="font-size: 0.85rem; color: var(--text-muted); margin: 4px 0;">
                    Always consult with qualified mortgage and real estate professionals for official guidance.
                </p>
            </div>
        </div>
    </div>
</body>
</html>`;

    return html;
  }, [formatCurrency, formatPercent]);

  const downloadFile = (content: string, filename: string, mimeType: string) => {
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const generateReport = useCallback(async (
    format: ReportFormat,
    data: ReportData
  ) => {
    setIsGenerating(true);
    
    try {
      // Track risk assessment on report generation
      if (data.formData && data.results) {
        const annualIncome = data.formData.primaryIncome?.annualIncome || 100000;
        const mockMortgageData = {
          homePrice: data.formData.homePrice || 0,
          downPayment: data.formData.downPayment || 0,
          downPaymentPercentage: data.formData.downPaymentPercentage || 20,
          sellerCredit: data.formData.sellerCredit || 0,
          interestRate: data.formData.interestRate || 6.5,
          loanTerm: data.formData.loanTerm || 30,
          loanType: data.formData.loanType || 'conventional',
          zipCode: data.formData.zipCode || '',
          state: data.formData.state || '',
          monthlyPayment: {
            principal: data.results.monthlyPayment?.principal || 0,
            interest: data.results.monthlyPayment?.interest || 0,
            taxes: data.results.monthlyPayment?.taxes || 0,
            insurance: data.results.monthlyPayment?.insurance || 0,
            pmi: data.results.monthlyPayment?.pmi || 0,
            hoa: data.results.monthlyPayment?.hoa || 0,
            total: data.results.monthlyPayment?.total || 0
          },
          closingCosts: {
            lenderFees: data.results.closingCosts?.lenderFees || 0,
            thirdPartyFees: data.results.closingCosts?.thirdPartyFees || 0,
            governmentFees: data.results.closingCosts?.governmentFees || 0,
            prepaidItems: data.results.closingCosts?.prepaidItems || 0,
            escrowItems: data.results.closingCosts?.escrowItems || 0,
            total: data.results.closingCosts?.total || 0
          },
          totalCashDue: data.results.totalCashDue || 0,
          isFirstTimeHomeBuyer: false,
          hasVABenefits: false,
          creditScore: 700,
          annualIncome: annualIncome,
          monthlyDebt: 0,
          estimatedHomeValue: data.formData.homePrice || 0,
          loanAmount: (data.formData.homePrice || 0) - (data.formData.downPayment || 0)
        } as any; // Type assertion to bypass strict typing for analytics purposes
        
        const riskAssessment = enhancedRiskAssessmentService.calculateRiskAssessment(mockMortgageData, annualIncome);
        trackReportGeneration(riskAssessment);
      }
      const timestamp = new Date().toLocaleDateString().replace(/\//g, '-');
      let content: string;
      let filename: string;
      let mimeType: string;

      switch (format) {
        case 'txt':
          content = generateTextReport(data);
          filename = `equiforge-unified-report-${timestamp}.txt`;
          mimeType = 'text/plain';
          break;

        case 'csv':
          content = generateCSVReport(data);
          filename = `equiforge-unified-report-${timestamp}.csv`;
          mimeType = 'text/csv';
          break;

        case 'json':
          content = generateJSONReport(data);
          filename = `equiforge-unified-report-${timestamp}.json`;
          mimeType = 'application/json';
          break;

        case 'html':
          content = generateHTMLReport(data);
          filename = `equiforge-unified-report-${timestamp}.html`;
          mimeType = 'text/html';
          break;

        case 'xlsx':
          // For now, we'll generate CSV for Excel compatibility
          content = generateCSVReport(data);
          filename = `equiforge-unified-report-${timestamp}.csv`;
          mimeType = 'text/csv';
          toast({
            title: "Excel Format",
            description: "Excel format generated as CSV - you can open this in Excel or Google Sheets."
          });
          break;

        default:
          throw new Error(`Unsupported format: ${format}`);
      }

      downloadFile(content, filename, mimeType);

      toast({
        title: "Report Generated Successfully",
        description: `Your ${format.toUpperCase()} report has been downloaded as ${filename}`
      });

      return filename;
    } catch (error) {
      console.error('Error generating report:', error);
      toast({
        title: "Report Generation Failed",
        description: error instanceof Error ? error.message : "An unknown error occurred",
        variant: "destructive"
      });
      throw error;
    } finally {
      setIsGenerating(false);
    }
  }, [generateTextReport, generateCSVReport, generateJSONReport, generateHTMLReport, toast]);

  return {
    generateReport,
    isGenerating
  };
};